import React, { Component, ReactNode } from 'react'
import Button from 'core/components/buttons/Button'
import Icon from 'core/components/icons/Icon'
import { datadogLogs } from '@datadog/browser-logs'
import { isChunkLoadError } from 'utils/Functions'

interface Props {
  children?: ReactNode
}

interface State {
  hasError: boolean
  isChunkError: boolean
  id: number
}

/**
 * Top-level error boundary that catches all unhandled React errors.
 *
 * Recovery strategy:
 * - **ChunkLoadError** (failed lazy-loaded chunk): shows hard-refresh instructions
 *   after the network retry logic in `lazyLoadHelper` has already exhausted all attempts.
 * - **All other errors**: logs to Datadog with a timestamped error ID for support
 *   reference and renders a generic error UI.
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    isChunkError: false,
    id: 0
  }

  /**
   * Synchronously derives error state from the thrown error.
   * Runs during the render phase, so side effects must go in `componentDidCatch`.
   */
  public static getDerivedStateFromError(error: Error): State {
    const id = new Date().getTime()
    return { hasError: true, isChunkError: isChunkLoadError(error), id }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    datadogLogs.logger.error('Error boundary - UI error', {
      error: {
        message: error.message,
        name: error.name,
        stack: error.stack,
        id: this.state.id,
        pathname: window?.location?.pathname,
        search: window?.location?.search
      },
      componentStack: errorInfo.componentStack,
      source: 'error-boundary'
    })
  }

  public render() {
    if (this.state.hasError) {
      const isMac = /mac/i.test(navigator.userAgent)
      return (
        <div className="flex justify-center items-center gap-5 w-screen h-[100vh] flex-col text-center">
          <Icon
            name="exclamation-mark"
            color="text-white"
            className="bg-dark-4 rounded-full p-1"
          />
          <h1 className="text-dark-4 m-0">Server error</h1>
          <div>
            {this.state.isChunkError ? (
              <>
                <p>The page could not load its resources.</p>
                <p>
                  Please try a hard refresh (
                  {isMac ? 'Cmd+Shift+R' : 'Ctrl+Shift+R'}) and if the problem
                  continues{' '}
                  <a href="mailto:support@everlance.com" className="underline">
                    contact support
                  </a>
                </p>
              </>
            ) : (
              <>
                <p>Oh no! Looks like something broke.</p>
                <p>
                  Try refreshing the page and if you need more help{' '}
                  <a href="mailto:support@everlance.com" className="underline">
                    contact support
                  </a>
                </p>
              </>
            )}
            <p>
              and reference this number{' '}
              <span className="text-dark-4">{this.state.id}</span>.
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => window.location.assign('/dashboard')}
          >
            Back to home
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
